import React, { useEffect, useState, useRef, useCallback } from 'react';
import AsyncSelect from "react-select/async";
import { customFetch } from '../apiClient';
import PaymentReceipt from "./PaymentReceipt";

// ✅ DYNAMIC STYLES FOR REACT SELECT (Updated to Compact 26px standard)
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '26px',
    height: '26px',
    fontSize: '11px',
    borderRadius: '4px',
    borderColor: state.isFocused ? 'var(--theme-color, #0088cc)' : '#cbd5e0',
    boxShadow: state.isFocused ? '0 0 0 2px color-mix(in srgb, var(--theme-color, #0088cc), transparent 85%)' : 'none',
    '&:hover': { borderColor: 'var(--theme-color, #0088cc)' },
    padding: '0'
  }),
  valueContainer: (base) => ({ ...base, padding: '0 6px', height: '24px' }),
  indicatorsContainer: (base) => ({ ...base, height: '24px' }),
  dropdownIndicator: (base) => ({ ...base, padding: '0 4px' }),
  clearIndicator: (base) => ({ ...base, padding: '0 4px' }),
  input: (base) => ({ ...base, margin: '0', padding: '0', fontSize: '11px' }),
  menu: (base) => ({ ...base, zIndex: 99999, borderRadius: '4px' }),
  menuPortal: (base) => ({ ...base, zIndex: 99999 }),
  option: (base, state) => ({
    ...base,
    fontSize: '11px',
    padding: '6px 10px',
    backgroundColor: state.isSelected 
      ? 'var(--theme-color, #0088cc)' 
      : state.isFocused 
        ? 'color-mix(in srgb, var(--theme-color, #0088cc), white 90%)' 
        : 'white',
    color: state.isSelected ? 'white' : '#333',
    cursor: 'pointer',
    '&:active': { backgroundColor: 'var(--theme-color, #0088cc)', color: 'white' }
  }),
  placeholder: (base) => ({ ...base, color: '#a0aec0', fontSize: '11px' })
};

const getCurrentDateTime = () => {
  const now = new Date();
  // Timezone offset adjust karna padta hai taaki local time sahi dikhe
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset()); 
  return now.toISOString().slice(0, 16); // Returns "YYYY-MM-DDTHH:mm"
};

const BillingPayment = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [customerResult, setCustomerResult] = useState(null);
  const [spCustomerData, setSpCustomerData] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [latestPayment, setLatestPayment] = useState(null);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const token = sessionStorage.getItem("token");

  // Payment form states
  const [paymentRemarks, setPaymentRemarks] = useState("");
  const [paymentCash, setPaymentCash] = useState(0);
  const [paymentUpi, setPaymentUpi] = useState(0);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  const [paymentSuccess, setPaymentSuccess] = useState("");
  const [paymentPayDate, setPaymentPayDate] = useState(getCurrentDateTime());

  // Edit payment states
  const [editPaymentId, setEditPaymentId] = useState(null);
  const [editForm, setEditForm] = useState({
    payDate: "", remarks: "", recvdby: "", upi: 0, received: 0, cash: 0,
  });
  const [editLoading, setEditLoading] = useState(false);

  const formRef = useRef(null);
  const payDateRef = useRef(null); 

  // Focus Logic
  useEffect(() => {
    if (customerResult && payDateRef.current) {
      setTimeout(() => {
        payDateRef.current.focus();
      }, 100);
    }
  }, [customerResult]);

  // Initial Form Focus
  useEffect(() => {
    if (formRef.current) {
      const firstInput = formRef.current.querySelector(
        'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea, button:not([disabled])'
      );
      firstInput?.focus();
    }
  }, []); 

  // Reset fields on new customer
  useEffect(() => {
    if (customerResult) {
      setPaymentRemarks("");
      setPaymentCash(0);
      setPaymentUpi(0);
      setPaymentPayDate(getCurrentDateTime());
    }
  }, [customerResult]);

  // Keyboard Navigation
  const handleKeyDown = useCallback((e) => {
    // Prevent interference with table inputs
    if (e.target.closest('.table-input')) return;

    const focusableElements = formRef.current?.querySelectorAll(
      'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea, button:not([disabled])'
    );
    if (!focusableElements || focusableElements.length === 0) return;
    const currentIndex = Array.from(focusableElements).indexOf(e.target);

    if (e.key === 'Enter' && currentIndex === focusableElements.length - 1) {
      e.target.closest('form').requestSubmit(); return;
    }
    if (['Enter', 'ArrowDown'].includes(e.key)) {
      e.preventDefault();
      const nextIndex = Math.min(focusableElements.length - 1, currentIndex + 1);
      focusableElements[nextIndex]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prevIndex = Math.max(0, currentIndex - 1);
      focusableElements[prevIndex]?.focus();
    }
  }, []);

  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (formRef.current && e.target.closest('form') === formRef.current) {
        handleKeyDown(e);
      }
    };
    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    if (showReceipt && !companyDetails) {
      const hid = sessionStorage.getItem("hospitalId");
      if (hid) {
        customFetch(`/Company/${hid}`)
          .then(res => res.json()).then(data => setCompanyDetails(data)).catch(console.error);
      }
    }
  }, [showReceipt, companyDetails, token]);

  const loadCustomerOptions = async (inputValue) => {
    if (!inputValue) return [];
    try {
    const res = await customFetch(`/BillingPayment/search-by-customer?search=${encodeURIComponent(inputValue)}`);
      if (!res.ok) throw new Error("Failed to load customers");
      const data = await res.json();
      
      const uniqueCustomers = [];
      const seenIds = new Set();
      
      data.forEach(item => {
          if (!seenIds.has(item.customer.cid)) {
              seenIds.add(item.customer.cid);
              uniqueCustomers.push({
                  label: `${item.customer.custName} (${item.customer.mobileNo})`,
                  value: item.customer.cid, 
                  data: item
              });
          }
      });
      return uniqueCustomers;
    } catch (e) { return []; }
  };

  const refreshCustomerData = async (cid, fallbackData = null) => {
    setLoading(true);
    try {
      const efPromise = customFetch(`/BillingPayment/search-by-customer?cid=${cid}`).then(res => res.json());

       const spPromise = customFetch(`/BillingPayment/customer-due-details/${cid}`).then(res => {
             if (!res.ok) return []; 
             return res.json();
        });

        const [efData, spData] = await Promise.all([efPromise, spPromise]);

        if (Array.isArray(efData) && efData.length > 0) {
            setCustomerResult(efData[0]); 
        } else if (fallbackData) {
            setCustomerResult(fallbackData);
        }

        if (Array.isArray(spData) && spData.length > 0) {
            setSpCustomerData(spData[0]); 
        } else if (fallbackData) {
            setSpCustomerData({
                Dues: 0,
                CUSTNAME: fallbackData.customer?.custName,
                MOBILE: fallbackData.customer?.mobileNo,
                Address: fallbackData.customer?.cAddress,
                Age: fallbackData.customer?.age
            });
        }

    } catch (err) {
        console.error("Error fetching details:", err);
        if(fallbackData) setCustomerResult(fallbackData);
    } finally {
        setLoading(false);
    }
  };

  const handleCustomerSelect = async (selected) => {
    if (!selected) { 
        setCustomerResult(null); 
        setSpCustomerData(null); 
        setSearchTerm(""); 
        setError(""); 
        return; 
    }
    setShowHistory(false);
    const cid = selected.value || selected.customer?.cid;
    const label = selected.label || (selected.customer ? `${selected.customer.custName} (${selected.customer.mobileNo})` : "");

    setSearchTerm(label);
    
    if (selected.data) {
        setCustomerResult(selected.data);
        setSpCustomerData({
            Dues: 0, 
            CUSTNAME: selected.data.customer?.custName,
            MOBILE: selected.data.customer?.mobileNo,
            Address: selected.data.customer?.cAddress,
            Age: selected.data.customer?.age
        });
    }

    await refreshCustomerData(cid, selected.data);
  };

  const handleAddPayment = async () => {
    setPaymentError("");
    if (!customerResult || !customerResult.customer) { setPaymentError("Select a customer first."); return; }
    if (paymentCash <= 0 && paymentUpi <= 0) { setPaymentError("Please enter cash or UPI amount."); return; }
    
    setPaymentLoading(true);

    const currentIpdId = customerResult.customer?.ipdId || customerResult.customer?.IpdId 
      || (customerResult.billSummaries?.length > 0 
            ? (customerResult.billSummaries[0].summary.ipdId || customerResult.billSummaries[0].summary.IpdId) 
            : null);

    const payload = {
      payDate: paymentPayDate, patID: 0, remarks: paymentRemarks.trim(),
      upi: Number(paymentUpi), cid: customerResult.customer.cid, hid: customerResult.customer.hid,
      received: Number(paymentCash) + Number(paymentUpi),
      frid: customerResult.billSummaries && customerResult.billSummaries.length > 0 ? customerResult.billSummaries[0].summary.frid : 0,
     cash: Number(paymentCash),
    
    ipdId: currentIpdId // ✅ FIXED: Sahi IPD ID attach karna
    };

    try {
   const res = await customFetch('/BillingPayment', {
  method: "POST",
  body: JSON.stringify(payload),
});
      if (!res.ok) throw new Error((await res.text()) || "Failed to save payment.");
      
      const savedPayment = await res.json();
      setLatestPayment(savedPayment); 
      setShowReceipt(true); 
      setPaymentSuccess("Payment saved successfully.");
      
      await refreshCustomerData(customerResult.customer.cid, customerResult);

      setPaymentRemarks(""); setPaymentCash(0); setPaymentUpi(0);
    } catch (err) { setPaymentError(err.message); } finally { setPaymentLoading(false); }
  };

  const received = Number(paymentCash) + Number(paymentUpi);
  const totalDue = spCustomerData?.Dues || spCustomerData?.dues || 0;

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => {
      const updatedForm = { ...prev, [name]: value };
      if (name === "cash" || name === "upi") {
        const cashVal = name === "cash" ? Number(value) : Number(prev.cash || 0);
        const upiVal = name === "upi" ? Number(value) : Number(prev.upi || 0);
        updatedForm.received = cashVal + upiVal;
      }
      return updatedForm;
    });
  };

  const handleEditPayment = async (payId) => {
    setEditLoading(true);
    try {
     const res = await customFetch(`/BillingPayment/${payId}`);
      if (!res.ok) throw new Error("Failed to fetch payment details.");
      const data = await res.json();
      let formattedDate = "";
    if (data.payDate) {
        const d = new Date(data.payDate);
        d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
        formattedDate = d.toISOString().slice(0, 16);
    }
      setEditForm({
        payDate: formattedDate,
        patID: data.patID || 0, remarks: data.remarks || "", recvdby: data.recvdby || "",
        deleted: data.deleted || 0, payMode: data.payMode || "", upi: data.upi || 0,
        cid: data.cid || 0, hid: data.hid || 0, received: data.received || 0,
        frid: data.frid || 0, cash: data.cash || 0, payId: data.payId,ipdId: data.ipdId || data.IpdId || null,
      });
      setEditPaymentId(payId);
      
      setTimeout(() => {
        document.getElementById(`edit-payDate-${payId}`)?.focus();
      }, 100);

    } catch (e) { alert(e.message); } finally { setEditLoading(false); }
  };

  const handleTableKeyDown = (e, field, payId) => {
    if (e.key === 'Enter') {
        e.preventDefault(); 
        if (field === 'payDate') document.getElementById(`edit-upi-${payId}`)?.focus();
        else if (field === 'upi') document.getElementById(`edit-cash-${payId}`)?.focus();
        else if (field === 'cash') document.getElementById(`edit-remarks-${payId}`)?.focus();
        else if (field === 'remarks') handleSaveEdit(); 
    }
  };

  const handleSaveEdit = async () => {
    if (!editPaymentId) return;
    try {
      setEditLoading(true);
      const res = await customFetch(`/BillingPayment/${editPaymentId}`, {
        method: "PUT",
        body: JSON.stringify(editForm),
      });
      if (!res.ok) throw new Error((await res.text()) || "Failed to update payment.");
      
      alert("Payment updated successfully.");
      setEditPaymentId(null); 

      if (customerResult?.customer?.cid) {
          await refreshCustomerData(customerResult.customer.cid, customerResult);
      }

    } catch (e) { alert(e.message); } finally { setEditLoading(false); }
  };

  const handleDeletePayment = async (payId) => {
    if (!window.confirm("Are you sure you want to delete this payment?")) return;
    try {
    const res = await customFetch(`/BillingPayment/${payId}`, {
  method: "DELETE"
});
      if (!res.ok) throw new Error("Failed to delete payment");

      if (customerResult?.customer?.cid) {
        await refreshCustomerData(customerResult.customer.cid, customerResult);
      }
    } catch (e) { alert(e.message); }
  };

  const handleCancelEdit = () => { setEditPaymentId(null); };

  const allPayments = (() => {
    if (!customerResult?.billSummaries) return [];
    const flattened = customerResult.billSummaries.flatMap(bs => bs.payments || []);
    const uniqueMap = {};
    for (const payment of flattened) { uniqueMap[payment.payId] = payment; }
    return Object.values(uniqueMap);
  })();

  return (
    <div className="billing-payment-container">
      <div className="billing-payment-card">
        <div className="billing-header">
          <div className="header-left">
            <div className="header-icon"><i className="bi bi-wallet2"></i></div>
            <div className="header-text"><h5>Billing Payments</h5><p>Search customer and manage payments</p></div>
          </div>
        </div>

        <div className="card-content">
          <div className="search-section">
            <label className="search-label"><i className="bi bi-search"></i> Search Customer</label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <div style={{ flex: 1 }}>
                <AsyncSelect
                  cacheOptions
                  loadOptions={loadCustomerOptions}
                  onChange={handleCustomerSelect}
                  placeholder="Search by Customer Name or Mobile..."
                  isClearable
                  styles={selectStyles}
                  value={searchTerm ? { label: searchTerm, value: customerResult?.customer?.cid } : null}
                  menuPortalTarget={document.body}
                  classNamePrefix="react-select"
                />
              </div>
              <button 
                  type="button" 
                  className="btn-submit" 
                  onClick={() => setShowHistory(true)} 
                  disabled={!customerResult} 
                  style={{ height: '26px', whiteSpace: 'nowrap', padding: '0 10px', display: 'flex', alignItems: 'center' }}
                >
                <i className="bi bi-clock-history me-1"></i> History
              </button>
            </div>
          </div>
        

          {error && <div className="alert-custom alert-danger"><i className="bi bi-exclamation-triangle-fill"></i> {error}</div>}
          {loading && <div className="text-center p-2 text-muted" style={{ fontSize: '11px' }}>Loading customer details...</div>}

          {customerResult && spCustomerData && (
            <>
              {/* Add Payment Form */}
              <div className="form-section">
                <div className="section-title"><i className="bi bi-plus-circle"></i><span>Add Payment</span></div>
                <div className="section-content">
                    {paymentError && <div className="alert-custom alert-danger py-1"><i className="bi bi-exclamation-triangle-fill"></i> {paymentError}</div>}
                    {paymentSuccess && <div className="alert-custom alert-success py-1"><i className="bi bi-check-circle-fill"></i> {paymentSuccess}</div>}
                    
                    <form ref={formRef} onSubmit={(e) => { e.preventDefault(); handleAddPayment(); }}>
                        <div className="form-row five-col">
                            <div className="form-group"><label className="form-label">Name</label><input type="text" className="form-input readonly" value={spCustomerData.CUSTNAME || spCustomerData.custname || ""} readOnly /></div>
                            <div className="form-group"><label className="form-label">Mobile</label><input type="text" className="form-input readonly" value={spCustomerData.MOBILE || spCustomerData.mobile || ""} readOnly /></div>
                            <div className="form-group"><label className="form-label">Age</label><input type="text" className="form-input readonly" value={spCustomerData.Age || spCustomerData.age || ""} readOnly /></div>
                            <div className="form-group"><label className="form-label">Address</label><input type="text" className="form-input readonly" value={spCustomerData.Address || spCustomerData.address || ""} readOnly /></div>
                            <div className="form-group"><label className="form-label text-danger">Total Due</label><input type="number" className="form-input readonly text-danger fw-bold" value={totalDue.toFixed(2)} readOnly /></div>
                        </div>

                        <div className="form-row five-col align-items-end mt-2">
                            <div className="form-group">
                              <label className="form-label">Pay Date</label>
                              <input 
                                  id="payDateInput" 
                                  ref={payDateRef} 
                                  type="datetime-local" 
                                  className="form-input" 
                                  value={paymentPayDate} 
                                  onChange={(e) => setPaymentPayDate(e.target.value)} 
                              />
                          </div>
                            <div className="form-group"><label className="form-label">Cash <span className="required">*</span></label><input type="number" className="form-input" min="0" step="0.01" value={paymentCash === 0 ? "" : paymentCash} onChange={(e) => setPaymentCash(e.target.value === "" ? 0 : Number(e.target.value))} placeholder="0.00" /></div>
                            <div className="form-group"><label className="form-label">UPI <span className="required">*</span></label><input type="number" className="form-input" min="0" step="0.01" value={paymentUpi === 0 ? "" : paymentUpi} onChange={(e) => setPaymentUpi(e.target.value === "" ? 0 : Number(e.target.value))} placeholder="0.00" /></div>
                            <div className="form-group"><label className="form-label">Received</label><input type="number" className="form-input readonly fw-bold text-success" value={received.toFixed(2)} readOnly /></div>
                            <div className="form-group"><label className="form-label">Remarks</label><input type="text" className="form-input" value={paymentRemarks} onChange={(e) => setPaymentRemarks(e.target.value)} placeholder="Comments..." /></div>
                        </div>
                        
                        <div className="text-end mt-2 pt-2 border-top">
                            <button type="submit" className="btn-submit" disabled={paymentLoading}>{paymentLoading ? "Saving..." : "Save Payment"}</button>
                        </div>
                    </form>
                </div>
              </div>
           {showHistory && (
            <>
              {/* Bill Summary Table */}
              <div className="form-section mt-2">
                 <div className="section-title"><i className="bi bi-receipt"></i><span>Bill Summary</span></div>
                 <div className="section-content p-0">
                    <div className="table-responsive">
                        <table className="custom-table">
                            <thead>
                                <tr>
                                    <th className="text-center" width="80">Bill No</th>
                                    <th className="text-right">Total</th>
                                    <th className="text-right">Received</th>
                                    <th className="text-right">Cash</th>
                                    <th className="text-right">UPI</th>
                                    <th className="text-right">Due</th>
                                    <th className="text-center">Bill Date</th>
                                    <th className="text-right">Disc (₹)</th>
                                    <th className="text-left">Authority</th>
                                    <th className="text-left">Req. By</th>
                                </tr>
                            </thead>
                            <tbody>
                                {customerResult.billSummaries && customerResult.billSummaries.length > 0 ? (
                                    customerResult.billSummaries.map((bill, index) => (
                                        <tr key={bill.summary.billNo || index}>
                                            <td className="text-center">{bill.summary.billNo}</td>
                                            <td className="text-right fw-bold">₹{bill.summary.total}</td>
                                            <td className="text-right text-success">₹{bill.summary.received}</td>
                                            <td className="text-right">{bill.summary.cash}</td>
                                            <td className="text-right">{bill.summary.upi}</td>
                                            <td className="text-right fw-bold" style={{color: bill.summary.due > 0 ? '#e74c3c' : '#27ae60'}}>₹{bill.summary.due}</td>
                                            <td className="text-center">{new Date(bill.summary.billDate).toLocaleDateString()}</td>
                                            <td className="text-right">₹{bill.summary.dis}</td>
                                            <td className="text-left text-muted">{bill.summary.disAuthority || "-"}</td>
                                            <td className="text-left text-muted">{bill.summary.requestedBy || "-"}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr><td colSpan="10" className="text-center p-3 text-muted">No bills found</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                 </div>
              </div>

              {/* Payment History Table */}
              <div className="form-section mt-2 mb-2">
                 <div className="section-title"><i className="bi bi-clock-history"></i><span>Payment History</span></div>
                 <div className="section-content p-0">
                    <div className="table-responsive">
                        <table className="custom-table">
                            <thead>
                                <tr>
                                    <th className="text-left">Pay Date</th>
                                    <th className="text-right">UPI</th>
                                    <th className="text-right">Cash</th>
                                    <th className="text-right">Received</th>
                                    <th className="text-left" width="30%">Remarks</th>
                                    <th className="text-center">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {allPayments.length > 0 ? allPayments.map((p) => (
                                    <tr key={p.payId}>
                                        {editPaymentId === p.payId ? (
                                            <>
                                                {/* EDIT MODE */}
                                               <td>
                                                    <input 
                                                        id={`edit-payDate-${p.payId}`} 
                                                        type="datetime-local"
                                                        className="table-input" 
                                                        value={editForm.payDate} 
                                                        onChange={handleEditFormChange} 
                                                        name="payDate" 
                                                        onKeyDown={(e) => handleTableKeyDown(e, 'payDate', p.payId)}
                                                        style={{ minWidth: '130px' }}
                                                    />
                                                </td>
                                                <td><input id={`edit-upi-${p.payId}`} type="number" className="table-input text-right" value={editForm.upi} onChange={handleEditFormChange} name="upi" onKeyDown={(e) => handleTableKeyDown(e, 'upi', p.payId)}/></td>
                                                <td><input id={`edit-cash-${p.payId}`} type="number" className="table-input text-right" value={editForm.cash} onChange={handleEditFormChange} name="cash" onKeyDown={(e) => handleTableKeyDown(e, 'cash', p.payId)}/></td>
                                                <td><input type="number" className="table-input readonly text-right fw-medium text-success" value={editForm.received} readOnly tabIndex="-1"/></td>
                                                <td><input id={`edit-remarks-${p.payId}`} type="text" className="table-input" value={editForm.remarks} onChange={handleEditFormChange} name="remarks" onKeyDown={(e) => handleTableKeyDown(e, 'remarks', p.payId)}/></td>
                                                <td className="text-center">
                                                    <div className="btn-group-actions">
                                                        <button type="button" className="btn-icon btn-save" onClick={handleSaveEdit} title="Save"><i className="bi bi-check-lg"></i></button>
                                                        <button type="button" className="btn-icon btn-cancel" onClick={handleCancelEdit} title="Cancel"><i className="bi bi-x-lg"></i></button>
                                                    </div>
                                                </td>
                                            </>
                                        ) : (
                                            <>
                                                {/* VIEW MODE */}
                                                <td className="text-left">{p.payDate ? new Date(p.payDate).toLocaleString() : ""}</td>
                                                <td className="text-right">{p.upi}</td>
                                                <td className="text-right">{p.cash}</td>
                                                <td className="text-right fw-medium text-success">{p.received}</td>
                                                <td className="text-left text-muted small">{p.remarks || "-"}</td>
                                                <td className="text-center">
                                                    <div className="btn-group-actions">
                                                        <button type="button" className="btn-icon btn-edit" onClick={() => handleEditPayment(p.payId)} title="Edit"><i className="bi bi-pencil-square"></i></button>
                                                        <button type="button" className="btn-icon btn-delete" onClick={() => handleDeletePayment(p.payId)} title="Delete"><i className="bi bi-trash"></i></button>
                                                    </div>
                                                </td>
                                            </>
                                        )}
                                    </tr>
                                )) : <tr><td colSpan="6" className="text-center p-3 text-muted">No payments found</td></tr>}
                            </tbody>
                        </table>
                    </div>
                 </div>
              </div>
              </>
              )}
            </>
          )}
        </div>
      </div>

      {showReceipt && companyDetails && customerResult?.customer && latestPayment && (
        <PaymentReceipt 
          show={true} 
          hospital={companyDetails} 
          customer={customerResult.customer} 
          payment={latestPayment} 
          onClose={() => setShowReceipt(false)} 
          onPrint={() => window.print()} 
        />
      )}

      {/* COMPACT STYLES */}
      <style jsx>{`
        *, *::before, *::after { box-sizing: border-box; }
        .billing-payment-container { padding: 0; font-family: system-ui, sans-serif; }
        .billing-payment-card { background: #fff; border-radius: 6px; box-shadow: 0 1px 8px rgba(0,0,0,0.06); border: 1px solid #e0e6ed; overflow: visible; }
        
        .billing-header { background: var(--theme-color, #0088cc); padding: 6px 12px; border-radius: 6px 6px 0 0; border-bottom: 2px solid rgba(0,0,0,0.1); }
        .header-left { display: flex; align-items: center; gap: 6px; color: white; }
        .header-icon { font-size: 14px; display: flex; align-items: center; }
        .header-text { display: flex; align-items: baseline; gap: 8px; }
        .header-text h5 { margin: 0; font-size: 13px; font-weight: 600; }
        .header-text p { margin: 0; font-size: 10px; opacity: 0.8; }
        
        .card-content { padding: 10px 12px; }
        
        .search-section { margin-bottom: 10px; width: 100%; max-width: 450px; }
        .search-label { display: flex; align-items: center; gap: 4px; font-size: 10px; font-weight: 600; color: #2c3e50; margin-bottom: 4px; }
        .search-label i { color: var(--theme-color, #0088cc); font-size: 11px; }
        
        .form-section { background: #f8f9fa; border: 1px solid #e0e6ed; border-radius: 5px; margin-bottom: 10px; overflow: visible; }
        .section-title { background: var(--theme-color, #0088cc); padding: 5px 10px; display: flex; align-items: center; gap: 5px; border-radius: 4px 4px 0 0; color: white; font-size: 11px; font-weight: 600; }
        .section-content { padding: 8px 10px; background: white; border-radius: 0 0 5px 5px; }
        
        .form-row { display: grid; gap: 8px; margin-bottom: 8px; align-items: start; }
        .five-col { grid-template-columns: repeat(5, 1fr); }
        .form-group { display: flex; flex-direction: column; }
        .form-label { font-size: 10px; font-weight: 600; color: #2c3e50; margin-bottom: 2px; }
        
        .form-input, .table-input { height: 26px; min-height: 26px; padding: 2px 6px; border: 1px solid #cbd5e0; border-radius: 4px; font-size: 11px; color: #2c3e50; outline: none; width: 100%; transition: none; }
        .form-input:focus, .table-input:focus { border-color: var(--theme-color, #0088cc); box-shadow: 0 0 0 2px rgba(0,136,204,0.15); }
        .readonly { background: #f3f4f6; cursor: default; color: #6c757d; }
        .required { color: #dc2626; }
        
        .btn-submit { height: 26px; padding: 0 14px; border-radius: 4px; border: none; background: var(--theme-color, #0088cc); color: white; font-size: 11px; font-weight: 600; cursor: pointer; transition: opacity 0.2s; }
        .btn-submit:hover:not(:disabled) { opacity: 0.9; }
        .btn-submit:disabled { opacity: 0.6; cursor: not-allowed; }
        
        .table-responsive { overflow-x: auto; border-radius: 0 0 5px 5px; }
        .custom-table { width: 100%; border-collapse: collapse; font-size: 11px; }
        .custom-table thead { background: #f1f5f9; border-bottom: 1px solid #cbd5e0; }
        .custom-table th { color: #2c3e50; padding: 6px 8px; text-align: left; font-weight: 600; font-size: 10px; text-transform: uppercase; white-space: nowrap; border: 1px solid #e0e6ed; }
        .custom-table td { padding: 4px 8px; border-bottom: 1px solid #e0e6ed; border-right: 1px solid #e0e6ed; vertical-align: middle; white-space: nowrap; }
        .custom-table td:last-child { border-right: none; }
        .custom-table tr:hover { background: #f8fafc; }
        
        .table-input { background: transparent; border: 1px solid transparent; height: 22px; min-height: 22px; padding: 0 4px; }
        .table-input:focus { background: white; border-color: var(--theme-color, #0088cc); }
        
        .text-center { text-align: center !important; }
        .text-right { text-align: right !important; }
        .text-left { text-align: left !important; }
        .text-muted { color: #64748b; }
        .fw-medium { font-weight: 600; }
        .fw-bold { font-weight: 700; }
        .text-success { color: #15803d !important; }
        .text-danger { color: #dc2626 !important; }
        
        .btn-icon { width: 22px; height: 22px; border-radius: 3px; border: none; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; font-size: 11px; margin: 0 2px; }
        .btn-edit { background: #e0f2fe; color: #0284c7; }
        .btn-edit:hover { background: #0284c7; color: white; }
        .btn-delete { background: #fee2e2; color: #dc2626; }
        .btn-delete:hover { background: #dc2626; color: white; }
        .btn-save { background: #dcfce7; color: #15803d; }
        .btn-save:hover { background: #15803d; color: white; }
        .btn-cancel { background: #f1f5f9; color: #64748b; }
        .btn-cancel:hover { background: #64748b; color: white; }
        .btn-group-actions { display: flex; justify-content: center; gap: 4px; }
        
        .alert-custom { padding: 6px 10px; border-radius: 4px; font-size: 11px; margin-bottom: 10px; display: flex; align-items: center; gap: 6px; }
        .alert-danger { background: #fee2e2; color: #b91c1c; border: 1px solid #fecaca; }
        .alert-success { background: #dcfce7; color: #15803d; border: 1px solid #bbf7d0; }
        
        @media (max-width: 768px) {
          .five-col { grid-template-columns: 1fr; }
          .custom-table { min-width: 800px; }
          .search-section { max-width: 100%; }
        }
      `}</style>
    </div>
  );
};

export default BillingPayment;
